import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  
  Card,
  CardContent,
  Chip,
  Button,
  Tabs,
  Tab,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Badge,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Build as BuildIcon,
  Home as HomeIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  Construction as ConstructionIcon,
  Extension as ExtensionIcon,
  HomeWork as HomeWorkIcon,
  AddHome as AddHomeIcon,
  Architecture as ArchitectureIcon
} from '@mui/icons-material';
import { DevelopmentOption } from '../types/api.types';

interface DevelopmentOptionsProps {
  options: DevelopmentOption[];
  recommended: string;
}

interface FloorPlanState {
  loading: boolean;
  data: any | null;
  error: string | null;
}

const DevelopmentOptions: React.FC<DevelopmentOptionsProps> = ({ options, recommended }) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedCards, setExpandedCards] = useState<{ [key: string]: boolean }>({});
  const [floorPlanStates, setFloorPlanStates] = useState<{ [key: string]: FloorPlanState }>({});
  const [floorPlanDialog, setFloorPlanDialog] = useState<{ open: boolean; scenario: string | null }>({ 
    open: false, 
    scenario: null 
  });

  // Early return if no options available
  if (!options || !Array.isArray(options) || options.length === 0) {
    return (
      <Paper sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Development Options Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Unable to analyze development options for this property.
          </Typography>
        </Box>
      </Paper>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatArea = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getScenarioIcon = (scenario: string) => {
    switch (scenario) {
      case 'renovation':
        return <BuildIcon />;
      case 'gut_renovation':
        return <ConstructionIcon />;
      case 'extension':
        return <ExtensionIcon />;
      case 'teardown':
      case 'teardown_max_density':
        return <HomeWorkIcon />;
      case 'adu':
        return <AddHomeIcon />;
      default:
        return <HomeIcon />;
    }
  };

  const getScenarioColor = (scenario: string) => {
    switch (scenario) {
      case 'renovation':
        return 'info';
      case 'gut_renovation':
        return 'primary';
      case 'extension':
        return 'secondary';
      case 'teardown':
      case 'teardown_max_density':
        return 'success';
      case 'adu':
        return 'warning';
      default:
        return 'default';
    }
  };

  const handleToggleExpand = (scenario: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [scenario]: !prev[scenario]
    }));
  };

  const handleViewFloorPlans = async (option: DevelopmentOption) => {
    // Include GFA and building footprint in the option ID for accurate floor plans
    const gfa = Math.round(option.total_gfa || 0);
    const footprint = Math.round(option.building_footprint || (gfa / (option.stories || 1)));
    const optionId = `${option.scenario}_${option.units}_${option.stories}_${gfa}_${footprint}`.replace(/\s+/g, '_');
    
    // Check if we already have the floor plan data
    if (floorPlanStates[optionId]?.data) {
      setFloorPlanDialog({ open: true, scenario: optionId });
      return;
    }

    // Set loading state
    setFloorPlanStates(prev => ({
      ...prev,
      [optionId]: { loading: true, data: null, error: null }
    }));

    try {
      // Fetch floor plan from the on-demand API endpoint with actual GFA data
      const response = await fetch(`/api/v1/floor-plans/${optionId}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch floor plan: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.status === 'success') {
        setFloorPlanStates(prev => ({
          ...prev,
          [optionId]: { 
            loading: false, 
            data: result.floor_plans, 
            error: null 
          }
        }));
        
        // Open the dialog to show the floor plan
        setFloorPlanDialog({ open: true, scenario: optionId });
        
        // Log cache status for debugging
        console.log(`Floor plan fetched for ${optionId}:`, {
          cached: result.cached,
          generationTime: result.generation_time_ms
        });
      } else {
        throw new Error(result.message || 'Failed to generate floor plan');
      }
    } catch (error) {
      console.error('Error fetching floor plan:', error);
      setFloorPlanStates(prev => ({
        ...prev,
        [optionId]: { 
          loading: false, 
          data: null, 
          error: error instanceof Error ? error.message : 'Failed to load floor plan' 
        }
      }));
    }
  };

  const handleCloseFloorPlanDialog = () => {
    setFloorPlanDialog({ open: false, scenario: null });
  };

  const allOptions = [...(options || [])].sort((a, b) => {
    // Sort by feasibility and ROI
    if (a.feasible !== b.feasible) return b.feasible ? 1 : -1;
    return (b.financial_metrics?.roi || 0) - (a.financial_metrics?.roi || 0);
  });

  const feasibleOptions = allOptions.filter(opt => opt.feasible);
  const notFeasibleOptions = allOptions.filter(opt => !opt.feasible);

  const displayOptions = selectedTab === 0 ? feasibleOptions : notFeasibleOptions;

  return (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Development Options Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Comprehensive analysis of all potential development scenarios
        </Typography>
      </Box>

      <Tabs
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        sx={{ mb: 3 }}
      >
        <Tab 
          label={`Feasible Options (${feasibleOptions.length})`}
          icon={<CheckCircleIcon />}
          iconPosition="start"
        />
        <Tab 
          label={`Not Feasible (${notFeasibleOptions.length})`}
          icon={<WarningIcon />}
          iconPosition="start"
        />
      </Tabs>

      <Grid container spacing={3}>
        {displayOptions.map((option) => {
          const isRecommended = option.scenario === recommended;
          const isExpanded = expandedCards[option.scenario] || false;

          return (
            <Grid item xs={12} key={option.scenario}>
              <Card 
                sx={{ 
                  position: 'relative',
                  border: isRecommended ? '2px solid' : '1px solid',
                  borderColor: isRecommended ? 'primary.main' : 'divider',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: 4,
                  }
                }}
              >
                {isRecommended && (
                  <Box sx={{ 
                    position: 'absolute', 
                    top: -12, 
                    right: 16,
                    zIndex: 1
                  }}>
                    <Badge badgeContent="Recommended" color="primary">
                      <StarIcon sx={{ color: 'primary.main' }} />
                    </Badge>
                  </Box>
                )}

                <CardContent>
                  <Grid container spacing={3}>
                    {/* Header Section */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            bgcolor: `${getScenarioColor(option.scenario)}.light`,
                            color: `${getScenarioColor(option.scenario)}.main`,
                            display: 'flex'
                          }}>
                            {getScenarioIcon(option.scenario)}
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {option.description}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip
                                label={option.feasible ? 'Feasible' : 'Not Feasible'}
                                size="small"
                                color={option.feasible ? 'success' : 'error'}
                              />
                              <Chip
                                label={`${option.units} Units`}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={`${option.stories} Stories`}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        </Box>
                        <IconButton onClick={() => handleToggleExpand(option.scenario)}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    </Grid>

                    {/* Key Metrics */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Total Investment
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatCurrency(option.cost_breakdown?.total_cost || option.financial_metrics?.total_cost || 0)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Projected Revenue
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatCurrency(option.financial_metrics?.revenue_projection || 0)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          ROI
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {((option.financial_metrics?.roi || 0) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Payback Period
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {(option.financial_metrics?.payback_period || 0).toFixed(1)} years
                        </Typography>
                      </Box>
                    </Grid>

                    {/* Progress Bar */}
                    <Grid item xs={12}>
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Building Area: {formatArea(option.total_gfa)} sq ft
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Cap Rate: {((option.financial_metrics?.cap_rate || 0) * 100).toFixed(1)}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min((option.financial_metrics?.roi || 0) * 100, 100)} 
                          sx={{ height: 6, borderRadius: 3 }}
                        />
                      </Box>
                    </Grid>

                    {/* Expanded Details */}
                    <Grid item xs={12}>
                      <Collapse in={isExpanded}>
                        <Box sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Grid container spacing={3}>
                            {/* Cost Breakdown */}
                            <Grid item xs={12} md={4}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                Cost Breakdown
                              </Typography>
                              <List dense>
                                <ListItem sx={{ px: 0 }}>
                                  <ListItemText 
                                    primary="Acquisition"
                                    secondary={formatCurrency(option.cost_breakdown?.acquisition || option.financial_metrics?.acquisition_cost || 0)}
                                  />
                                </ListItem>
                                <ListItem sx={{ px: 0 }}>
                                  <ListItemText 
                                    primary="Construction"
                                    secondary={formatCurrency(option.cost_breakdown?.construction || option.financial_metrics?.construction_cost || 0)}
                                  />
                                </ListItem>
                                <ListItem sx={{ px: 0 }}>
                                  <ListItemText 
                                    primary="Soft Costs"
                                    secondary={formatCurrency(option.cost_breakdown?.soft_costs || option.financial_metrics?.soft_costs || 0)}
                                  />
                                </ListItem>
                              </List>
                            </Grid>

                            {/* Opportunities */}
                            <Grid item xs={12} md={4}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                Opportunities
                              </Typography>
                              <List dense>
                                {(option.opportunities || []).map((opp, index) => (
                                  <ListItem key={index} sx={{ px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <CheckCircleIcon color="success" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={opp} />
                                  </ListItem>
                                ))}
                              </List>
                            </Grid>

                            {/* Challenges */}
                            <Grid item xs={12} md={4}>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                Challenges
                              </Typography>
                              <List dense>
                                {(option.challenges || []).map((challenge, index) => (
                                  <ListItem key={index} sx={{ px: 0 }}>
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                      <WarningIcon color="warning" fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText primary={challenge} />
                                  </ListItem>
                                ))}
                              </List>
                            </Grid>
                          </Grid>

                          {/* Action Buttons */}
                          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                            <Button 
                              variant="contained" 
                              startIcon={<MoneyIcon />}
                              sx={{ borderRadius: 2 }}
                            >
                              View Detailed Financials
                            </Button>
                            <Button 
                              variant="outlined" 
                              startIcon={
                                floorPlanStates[`${option.scenario}_${option.units}_${option.stories}`.replace(/\s+/g, '_')]?.loading ? 
                                <CircularProgress size={20} /> : 
                                <ArchitectureIcon />
                              }
                              sx={{ borderRadius: 2 }}
                              onClick={() => handleViewFloorPlans(option)}
                              disabled={floorPlanStates[`${option.scenario}_${option.units}_${option.stories}`.replace(/\s+/g, '_')]?.loading}
                            >
                              {floorPlanStates[`${option.scenario}_${option.units}_${option.stories}`.replace(/\s+/g, '_')]?.loading ? 
                                'Generating Floor Plans...' : 
                                'View Floor Plans'}
                            </Button>
                          </Box>
                        </Box>
                      </Collapse>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Floor Plan Dialog */}
      <Dialog 
        open={floorPlanDialog.open} 
        onClose={handleCloseFloorPlanDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArchitectureIcon />
          Floor Plans
        </DialogTitle>
        <DialogContent>
          {floorPlanDialog.scenario && floorPlanStates[floorPlanDialog.scenario]?.data ? (
            <Box>
              {/* Display floor plan data */}
              {floorPlanStates[floorPlanDialog.scenario].data.floors?.map((floor: any, index: number) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Floor {floor.floor_number} - {floor.floor_area} sq ft
                  </Typography>
                  
                  {/* Display SVG if available */}
                  {floor.svg && (
                    <Box 
                      sx={{ 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: 2, 
                        p: 2,
                        bgcolor: 'background.paper'
                      }}
                      dangerouslySetInnerHTML={{ __html: floor.svg }}
                    />
                  )}
                  
                  {/* Display rooms if available */}
                  {floor.rooms && floor.rooms.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Room Details:</Typography>
                      <Grid container spacing={1}>
                        {floor.rooms.map((room: any, roomIndex: number) => (
                          <Grid item xs={6} sm={4} md={3} key={roomIndex}>
                            <Chip 
                              label={`${room.type}: ${room.area} sq ft`}
                              variant="outlined"
                              size="small"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Box>
              ))}
              
              {/* Display summary stats */}
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Summary</Typography>
                <Typography variant="body2">
                  Total Area: {floorPlanStates[floorPlanDialog.scenario].data.total_sqft} sq ft
                </Typography>
                <Typography variant="body2">
                  Efficiency Ratio: {(floorPlanStates[floorPlanDialog.scenario].data.efficiency_ratio * 100).toFixed(1)}%
                </Typography>
              </Box>
            </Box>
          ) : floorPlanDialog.scenario && floorPlanStates[floorPlanDialog.scenario]?.error ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <WarningIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
              <Typography color="error">
                {floorPlanStates[floorPlanDialog.scenario]?.error}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Generating floor plans...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFloorPlanDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DevelopmentOptions;